import { useSnackbar } from '../components/snackbar';

/**
 * Extracts a human-readable message from an error, unwrapping SDK-level
 * wrappers like `FetchError` that carry the real cause in their `cause`
 * property.
 */
export const extractErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    if (error && typeof error === 'object') {
      const errorLike = error as Record<string, unknown>;
      if (typeof errorLike.message === 'string') {
        return errorLike.message;
      }
    }

    return String(error);
  }

  // The OpenAPI SDK wraps any error thrown by `fetchApi` inside a `FetchError`.
  // The original backend message lives in `cause`.  Walk the chain until we
  // find a leaf message that isn't the generic SDK placeholder.
  const SDK_PLACEHOLDER = 'The request failed and the interceptors did not return an alternative response';

  let current: unknown = error;
  while (current instanceof Error) {
    if (current.message && current.message !== SDK_PLACEHOLDER) {
      return current.message;
    }
    current = (current as any).cause;
  }

  // Fallback: return whatever the top-level message is
  return error.message;
};

export const useErrorNotification = () => {
  const { enqueueSnackbar } = useSnackbar();
  return {
    reportError: (error: unknown) =>
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' }),
  };
};
